import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    downloadPaymentReceiptBlob,
    fetchPaymentReceipt,
    getPaymentReceiptMimeType,
    getPaymentReceiptName,
} from '../../../utils/paymentReceiptStore';

const clampIndex = (value, length) => {
    if (!length) return 0;
    return Math.min(length - 1, Math.max(0, Number(value) || 0));
};

const formatBytes = (value) => {
    const bytes = Math.max(0, Number(value || 0));
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1).replace('.', ',')} МБ`;
};

const PaymentReceiptPreviewModal = ({
    show = false,
    paymentAccountId = '',
    receipts = [],
    initialIndex = 0,
    onClose,
}) => {
    const safeReceipts = useMemo(
        () => (Array.isArray(receipts) ? receipts.filter(Boolean) : []),
        [receipts]
    );
    const [activeIndex, setActiveIndex] = useState(0);
    const [asset, setAsset] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const objectUrlRef = useRef('');
    const requestRef = useRef(0);

    const activeReceipt = safeReceipts[activeIndex] || null;
    const receiptName = getPaymentReceiptName(activeReceipt, `Чек ${activeIndex + 1}`);
    const receiptSize = formatBytes(activeReceipt?.sizeBytes);
    const mimeType = String(asset?.mimeType || getPaymentReceiptMimeType(activeReceipt)).toLowerCase();
    const isImage = mimeType.startsWith('image/');
    const isPdf = mimeType === 'application/pdf';

    const revokeUrl = () => {
        if (!objectUrlRef.current) return;
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = '';
    };

    useEffect(() => {
        if (!show) return;
        setActiveIndex(clampIndex(initialIndex, safeReceipts.length));
    }, [initialIndex, safeReceipts.length, show]);

    useEffect(() => {
        if (!show || !paymentAccountId || !activeReceipt) {
            requestRef.current += 1;
            revokeUrl();
            setAsset(null);
            setError('');
            setLoading(false);
            return undefined;
        }

        const revision = requestRef.current + 1;
        requestRef.current = revision;
        let cancelled = false;

        revokeUrl();
        setAsset(null);
        setError('');
        setLoading(true);

        fetchPaymentReceipt({ paymentAccountId, receipt: activeReceipt })
            .then((result) => {
                if (cancelled || requestRef.current !== revision) return;

                const url = URL.createObjectURL(result.blob);
                objectUrlRef.current = url;
                setAsset({ ...result, url });
            })
            .catch((loadError) => {
                if (cancelled || requestRef.current !== revision) return;
                setError(
                    loadError?.response?.data?.message ||
                    loadError?.response?.data?.error ||
                    loadError?.message ||
                    'Не удалось открыть чек'
                );
            })
            .finally(() => {
                if (!cancelled && requestRef.current === revision) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [activeReceipt, paymentAccountId, show]);

    useEffect(() => () => {
        requestRef.current += 1;
        revokeUrl();
    }, []);

    useEffect(() => {
        if (!show) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose?.();
            if (event.key === 'ArrowLeft' && safeReceipts.length > 1) {
                setActiveIndex((current) => current <= 0 ? safeReceipts.length - 1 : current - 1);
            }
            if (event.key === 'ArrowRight' && safeReceipts.length > 1) {
                setActiveIndex((current) => current >= safeReceipts.length - 1 ? 0 : current + 1);
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose, safeReceipts.length, show]);

    if (!show) return null;

    const handleDownload = async () => {
        if (asset?.blob) {
            downloadPaymentReceiptBlob({
                blob: asset.blob,
                filename: asset.filename || receiptName,
            });
            return;
        }

        try {
            const result = await fetchPaymentReceipt({
                paymentAccountId,
                receipt: activeReceipt,
                download: true,
            });
            downloadPaymentReceiptBlob(result);
        } catch (downloadError) {
            setError(downloadError?.message || 'Не удалось скачать чек');
        }
    };

    return (
        <div
            className="paymentReceiptPreviewAdmin"
            role="dialog"
            aria-modal="true"
            aria-label="Просмотр чека"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose?.();
            }}
        >
            <div
                className="paymentReceiptPreviewAdmin__window"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="paymentReceiptPreviewAdmin__head">
                    <div>
                        <strong>Чек к платёжной операции</strong>
                        <span>
                            {receiptName}
                            {receiptSize ? ` · ${receiptSize}` : ''}
                            {safeReceipts.length > 1 ? ` · ${activeIndex + 1}/${safeReceipts.length}` : ''}
                        </span>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Закрыть чек">×</button>
                </div>

                <div className="paymentReceiptPreviewAdmin__body">
                    {loading ? (
                        <div className="paymentReceiptPreviewAdmin__state">Загружаем чек…</div>
                    ) : error ? (
                        <div className="paymentReceiptPreviewAdmin__state paymentReceiptPreviewAdmin__state--error">{error}</div>
                    ) : asset?.url && isImage ? (
                        <img src={asset.url} alt={receiptName} />
                    ) : asset?.url && isPdf ? (
                        <iframe src={asset.url} title={receiptName} />
                    ) : asset?.url ? (
                        <div className="paymentReceiptPreviewAdmin__state">
                            Предпросмотр этого типа файла недоступен. Используйте скачивание.
                        </div>
                    ) : (
                        <div className="paymentReceiptPreviewAdmin__state">Чек не выбран.</div>
                    )}

                    {safeReceipts.length > 1 && (
                        <>
                            <button
                                type="button"
                                className="paymentReceiptPreviewAdmin__nav paymentReceiptPreviewAdmin__nav--prev"
                                onClick={() => setActiveIndex((current) => current <= 0 ? safeReceipts.length - 1 : current - 1)}
                                aria-label="Предыдущий чек"
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                className="paymentReceiptPreviewAdmin__nav paymentReceiptPreviewAdmin__nav--next"
                                onClick={() => setActiveIndex((current) => current >= safeReceipts.length - 1 ? 0 : current + 1)}
                                aria-label="Следующий чек"
                            >
                                ›
                            </button>
                        </>
                    )}
                </div>

                <div className="paymentReceiptPreviewAdmin__footer">
                    <span>Чек загружается через защищённый API и не имеет публичной ссылки.</span>
                    <div>
                        <button type="button" onClick={onClose}>Закрыть</button>
                        <button type="button" onClick={handleDownload} disabled={loading || !activeReceipt}>Скачать</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentReceiptPreviewModal;
